import React from 'react'
import { useOutletContext, useNavigate } from 'react-router'
import { Page } from '../../Elements/PageMargins'
import DynamicBreadcrumbs from '../../Elements/DynamicBreadcrumbs'
import ContainedButton, {
  ContainedButtonVariant,
} from '../../Buttons/ContainedButton'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import { useTranslation } from 'react-i18next'
import CardFormHeader from '../../Card/CardFormHeader'
import Header, { HeaderVariant } from '../../Elements/Header'
import InternalEventInformationFields from './InternalEventInformationFields'
import InternalEventLocationFields from './InternalEventLocationFields'
import { Event } from '../../../swagger'

export interface InternalEventsOutletContext {
  isMobile: boolean
  canRegister: boolean
  isRegistering: boolean
  isRegistered: boolean
  isEventCreator?: boolean
  handleRegister: () => Promise<void>
  eventDetails?: Event
}

const InternalEventOverview: React.FC = () => {
  const {
    eventDetails,
    isMobile,
    canRegister,
    isRegistering,
    isRegistered,
    handleRegister,
  } = useOutletContext<InternalEventsOutletContext>()

  const { t } = useTranslation()
  const navigate = useNavigate()

  return (
    <Page withinTab>
      <DynamicBreadcrumbs
        breadcrumbs={[
          {
            label: t(
              'LeadershipTraining.Breadcrumb.Title',
              'Leadership Training'
            ),
            onClick: () => navigate('/events/leadership-training'),
          },
          { label: eventDetails?.name ?? '' },
        ]}
      />

      <Card sx={{ padding: 0, mb: 2 }}>
        <CardFormHeader
          header={
            <Header
              headerName={t('Events.EventDetails.CardHeader', 'Event Details')}
              component="h2"
              variant={HeaderVariant.Card}
            />
          }
          buttons={
            !isMobile ? (
              <ContainedButton
                id="leadership-register"
                variant={ContainedButtonVariant.Register}
                onClick={handleRegister}
                disabled={!canRegister || isRegistering || isRegistered}
              />
            ) : undefined
          }
          headerContainerProps={(theme) => ({
            ml: 1.5,
            mt: 2.5,
            mb: 0.25,
            [theme.breakpoints.down('sm')]: {
              mt: 1.25,
              mb: 0,
            },
          })}
        />
        <CardContent sx={{ pt: 1 }}>
          {isMobile && (
            <Box sx={{ px: 3, pb: 2 }}>
              <ContainedButton
                id="leadership-register-mobile"
                variant={ContainedButtonVariant.Register}
                onClick={handleRegister}
                disabled={!canRegister || isRegistering || isRegistered}
                fullWidth
              />
            </Box>
          )}
          {!!eventDetails && (
            <InternalEventInformationFields event={eventDetails} />
          )}
        </CardContent>
      </Card>

      <Card sx={{ padding: 0, mb: 2 }}>
        <CardContent sx={{ pt: 0, px: 0, pb: 0 }}>
          <Box sx={{ pt: 0, px: 3, pb: 3 }}>
            {!!eventDetails && (
              <InternalEventLocationFields event={eventDetails} />
            )}
          </Box>
        </CardContent>
      </Card>
    </Page>
  )
}

export default InternalEventOverview
